import { Outlet, RouterProvider, createRootRoute, createRoute, createRouter, useLocation, useNavigate } from '@tanstack/react-router'
import type { Router } from '@tanstack/react-router'
import React from 'react'
import { LanguageWrapper } from '@/components/LanguageWrapper'
import { Toaster } from '@/components/ui/sonner'
import { Analytics } from '@/components/Analytics'
import { MetaManager } from '@/components/MetaManager'
import { useWebVitals } from '@/hooks/useWebVitals'
import { getCanonicalUrl, getHreflangLinks } from '@/hooks/useCanonicalUrl'
import HomePage from '@/pages/Home'
import Challenge100 from '@/pages/Challenge100'
import ChallengeJourney from '@/pages/ChallengeJourney'
import HundredSeriesPage from '@/pages/HundredSeriesPage'
import Blog from '@/pages/Blog'
import BlogPost from '@/pages/BlogPost'
import Auth from '@/pages/Auth'
import { AdminDashboard } from '@/pages/admin/AdminDashboard'
import About from '@/pages/About'
import AboutTrauma from '@/pages/AboutTrauma'
import AboutEntrepreneur from '@/pages/AboutEntrepreneur'
import AboutResume from '@/pages/AboutResume'
import FAQ from '@/pages/FAQ'
import Methodology from '@/pages/Methodology'
import Pearl from '@/pages/Pearl'
import ColdStart from '@/pages/ColdStart'
import PrivacyPolicy from '@/pages/PrivacyPolicy'
import TermsOfService from '@/pages/TermsOfService'
import ComponentShowcase from '@/pages/ComponentShowcase'
import ProjectList from '@/pages/ProjectList'
import ProjectDetail from '@/pages/ProjectDetail'

function HeadLinks() {
  const location = useLocation()
  React.useEffect(() => {
    const lang = location.pathname.startsWith('/zh') ? 'zh' : 'en'
    const canonical = getCanonicalUrl(location.pathname, lang)
    const links = getHreflangLinks(location.pathname)
    const setLink = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang 
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      let link = document.querySelector(selector) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        if (hreflang) link.hreflang = hreflang;
        document.head.appendChild(link);
      }
      link.href = href;
    }
    setLink('canonical', canonical)
    setLink('alternate', links.en, 'en')
    setLink('alternate', links.zh, 'zh')
    setLink('alternate', links['x-default'], 'x-default')
  }, [location.pathname])
  return null
}

function WebVitalsTracker() {
  useWebVitals();
  return null;
}

function RootLayout() {
  return (
    <LanguageWrapper defaultLanguage="en">
      <HeadLinks />
      <MetaManager>
        <Analytics />
        <WebVitalsTracker />
        <Outlet />
      </MetaManager>
      <Toaster />
    </LanguageWrapper>
  )
}

const rootRoute = createRootRoute({
  component: RootLayout,
})

function EnglishRedirect() {
  const location = useLocation()
  const navigate = useNavigate()
  React.useEffect(() => {
    const newPath = location.pathname.replace(/^\/en/, '') || '/'
    navigate({ to: newPath + location.search + location.hash, replace: true })
  }, [location.pathname])
  return null
}

const baseChildren = [
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomePage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/en', component: () => <EnglishRedirect /> }),
  createRoute({ getParentRoute: () => rootRoute, path: '/en/*', component: () => <EnglishRedirect /> }),
  createRoute({ getParentRoute: () => rootRoute, path: '/challenge100', component: Challenge100 }),
  createRoute({ getParentRoute: () => rootRoute, path: '/challenge100/journey', component: ChallengeJourney }),
  createRoute({ getParentRoute: () => rootRoute, path: '/launch100', component: () => <HundredSeriesPage seriesId="launch100" /> }),
  createRoute({ getParentRoute: () => rootRoute, path: '/spy100', component: () => <HundredSeriesPage seriesId="spy100" /> }),
  createRoute({ getParentRoute: () => rootRoute, path: '/ship100', component: () => <HundredSeriesPage seriesId="ship100" /> }),
  createRoute({ getParentRoute: () => rootRoute, path: '/grow100', component: () => <HundredSeriesPage seriesId="grow100" /> }),
  createRoute({ getParentRoute: () => rootRoute, path: '/blog', component: Blog }),
  createRoute({ getParentRoute: () => rootRoute, path: '/blog/$slug', component: BlogPost }),
  createRoute({ getParentRoute: () => rootRoute, path: '/auth', component: Auth }),
  createRoute({ getParentRoute: () => rootRoute, path: '/admin', component: AdminDashboard }),
  createRoute({ getParentRoute: () => rootRoute, path: '/about', component: About }),
  createRoute({ getParentRoute: () => rootRoute, path: '/about/trauma', component: AboutTrauma }),
  createRoute({ getParentRoute: () => rootRoute, path: '/about/entrepreneur', component: AboutEntrepreneur }),
  createRoute({ getParentRoute: () => rootRoute, path: '/about/resume', component: AboutResume }),
  createRoute({ getParentRoute: () => rootRoute, path: '/faq', component: FAQ }),
  createRoute({ getParentRoute: () => rootRoute, path: '/methodology', component: Methodology }),
  createRoute({ getParentRoute: () => rootRoute, path: '/pearl', component: Pearl }),
  createRoute({ getParentRoute: () => rootRoute, path: '/cold-start', component: ColdStart }),
  createRoute({ getParentRoute: () => rootRoute, path: '/privacy-policy', component: PrivacyPolicy }),
  createRoute({ getParentRoute: () => rootRoute, path: '/terms-of-service', component: TermsOfService }),
  createRoute({ getParentRoute: () => rootRoute, path: '/components/entity-anchor', component: ComponentShowcase }),
  createRoute({ getParentRoute: () => rootRoute, path: '/$category', component: ProjectList }),
  createRoute({ getParentRoute: () => rootRoute, path: '/$category/$projectId', component: ProjectDetail }),
]

const zhParent = createRoute({ getParentRoute: () => rootRoute, path: '/zh', component: () => <Outlet /> })
const zhChildren = [
  createRoute({ getParentRoute: () => zhParent, path: '/', component: HomePage }),
  createRoute({ getParentRoute: () => zhParent, path: '/challenge100', component: Challenge100 }),
  createRoute({ getParentRoute: () => zhParent, path: '/challenge100/journey', component: ChallengeJourney }),
  createRoute({ getParentRoute: () => zhParent, path: '/launch100', component: () => <HundredSeriesPage seriesId="launch100" /> }),
  createRoute({ getParentRoute: () => zhParent, path: '/spy100', component: () => <HundredSeriesPage seriesId="spy100" /> }),
  createRoute({ getParentRoute: () => zhParent, path: '/ship100', component: () => <HundredSeriesPage seriesId="ship100" /> }),
  createRoute({ getParentRoute: () => zhParent, path: '/grow100', component: () => <HundredSeriesPage seriesId="grow100" /> }),
  createRoute({ getParentRoute: () => zhParent, path: '/blog', component: Blog }),
  createRoute({ getParentRoute: () => zhParent, path: '/blog/$slug', component: BlogPost }),
  createRoute({ getParentRoute: () => zhParent, path: '/auth', component: Auth }),
  createRoute({ getParentRoute: () => zhParent, path: '/admin', component: AdminDashboard }),
  createRoute({ getParentRoute: () => zhParent, path: '/about', component: About }),
  createRoute({ getParentRoute: () => zhParent, path: '/about/trauma', component: AboutTrauma }),
  createRoute({ getParentRoute: () => zhParent, path: '/about/entrepreneur', component: AboutEntrepreneur }),
  createRoute({ getParentRoute: () => zhParent, path: '/about/resume', component: AboutResume }),
  createRoute({ getParentRoute: () => zhParent, path: '/faq', component: FAQ }),
  createRoute({ getParentRoute: () => zhParent, path: '/methodology', component: Methodology }),
  createRoute({ getParentRoute: () => zhParent, path: '/pearl', component: Pearl }),
  createRoute({ getParentRoute: () => zhParent, path: '/cold-start', component: ColdStart }),
  createRoute({ getParentRoute: () => zhParent, path: '/privacy-policy', component: PrivacyPolicy }),
  createRoute({ getParentRoute: () => zhParent, path: '/terms-of-service', component: TermsOfService }),
  createRoute({ getParentRoute: () => zhParent, path: '/components/entity-anchor', component: ComponentShowcase }),
  createRoute({ getParentRoute: () => zhParent, path: '/$category', component: ProjectList }),
  createRoute({ getParentRoute: () => zhParent, path: '/$category/$projectId', component: ProjectDetail }),
]

const routeTree = rootRoute.addChildren([...baseChildren, zhParent.addChildren(zhChildren)])

export const router = createRouter({ routeTree } as any)

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function TSRProvider() {
  return <RouterProvider router={router} />
}
